import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withholdingsQueryKey } from './keys';
import type { WithholdingDto, WithholdingsHttpRequest } from '../types';

export interface UseDeleteWithholdingParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/withholdings`); se le añade `/{childId}`. */
  withholdingsBaseEndpoint: string;
  httpRequest: WithholdingsHttpRequest;
}

/**
 * Elimina una retención manual (`DELETE /{id}/withholdings/{childId}`) → 200 `{header}`.
 *
 * Solo las filas `isManual = true` son eliminables; el backend responde 409 sobre
 * automáticas/manipuladas (la UI ya deshabilita Eliminar en ese caso).
 *
 * - `onSuccess`: quita la fila del cache.
 * - `onSettled`: invalida `['document-withholdings', documentId]` (totales cambian).
 */
export function useDeleteWithholding({
  documentId,
  withholdingsBaseEndpoint,
  httpRequest,
}: UseDeleteWithholdingParams) {
  const queryClient = useQueryClient();
  const key = withholdingsQueryKey(documentId);

  return useMutation<string, Error, string>({
    mutationFn: async (withholdingId) => {
      await httpRequest('DELETE', `${withholdingsBaseEndpoint}/${withholdingId}`);
      return withholdingId;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData<WithholdingDto[]>(key, (prev) =>
        (prev ?? []).filter((row) => row.id !== deletedId),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
